import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BsLightbulb, BsBoxArrowUpRight } from 'react-icons/bs';
import { getDidYouKnow } from '../../api/knowledge';
import { trackEvent } from '../../utils/analytics';

export default function DidYouKnow() {
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDidYouKnow()
      .then((data) => {
        if (data?.id) {
          setSnippet(data);
          trackEvent('knowledge_did_you_know_viewed', {
            external_id: data.external_id,
            category: data.category,
            source: data.source?.name,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="glass rounded-2xl p-5 animate-pulse h-full">
        <div className="h-3 w-24 bg-white/10 rounded mb-3" />
        <div className="h-4 w-3/4 bg-white/10 rounded mb-2" />
        <div className="h-3 w-full bg-white/5 rounded mb-1" />
        <div className="h-3 w-4/5 bg-white/5 rounded" />
      </div>
    );
  }

  if (!snippet) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="glass rounded-2xl p-5 h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BsLightbulb className="text-yellow-400 text-base" />
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Did you know?</span>
        </div>
        {snippet.category && (
          <span className="text-[10px] font-medium text-brand-400 bg-brand-500/10 px-2 py-0.5 rounded-full">
            {snippet.category}
          </span>
        )}
      </div>

      {/* Title */}
      {snippet.title && (
        <h3 className="text-base font-bold text-white mb-2 font-display leading-snug">
          {snippet.title}
        </h3>
      )}

      {/* Content */}
      <p className="text-gray-400 text-[13px] leading-relaxed flex-1">
        {snippet.content || snippet.summary}
      </p>

      {/* Source */}
      {snippet.source?.name && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <a
            href={snippet.source.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('knowledge_source_clicked', {
              external_id: snippet.external_id,
              source: snippet.source.name,
            })}
            className="text-[11px] text-gray-500 hover:text-gray-300 transition-colors inline-flex items-center gap-1"
          >
            {snippet.source.name}
            <BsBoxArrowUpRight className="text-[9px]" />
          </a>
        </div>
      )}
    </motion.div>
  );
}
